import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  formatDay,
  monthRange,
  presetRange,
  punchWorkerId,
  todayIsoDate,
  useAttendancePunches,
  useAttendanceOrgTimeZone,
  type RangePreset,
} from '@/pages/attendance/data'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import { attendanceBoardApi, countRosterWorkers } from '@/services/attendance-board'
import { locationsApi } from '@/services/locations'
import { DEFAULT_OFFICE_HOURS, officeHoursApi } from '@/services/office-hours'
import { workersApi, type AttendanceReportWorker } from '@/services/workers'
import { useAuthStore } from '@/stores/auth-store'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKLY_OFF_KEY = 'attendance_report_weekly_off'
const MAX_RANGE_DAYS = 366

const REPORT_PERIODS: { group: string; options: [RangePreset, string][] }[] = [
  { group: 'Daily', options: [['today', 'Today'], ['yesterday', 'Yesterday']] },
  { group: 'Weekly', options: [['this-week', 'This week'], ['last-week', 'Last week']] },
  { group: 'Biweekly', options: [['this-fortnight', 'This 2 weeks'], ['last-fortnight', 'Previous 2 weeks']] },
  { group: 'Monthly', options: [['this-month', 'This month'], ['last-month', 'Last month'], ['month', 'Pick a month']] },
  { group: 'Other', options: [['custom', 'Custom range']] },
]
type WorkerScope = 'worked' | 'all'
type DemoLocation = {
  id: string
  place: string
  allocated: number
  attended: number
  own: number
  contractor: number
}

const DEMO_LOCATIONS: DemoLocation[] = [
  {
    id: 'demo-central-warehouse',
    place: 'Demo · Central Warehouse',
    allocated: 4,
    attended: 3,
    own: 3,
    contractor: 1,
  },
  {
    id: 'demo-riverside-venue',
    place: 'Demo · Riverside Venue',
    allocated: 3,
    attended: 2,
    own: 1,
    contractor: 2,
  },
]

function shortDate(iso: string) {
  return formatDay(`${iso}T00:00+00:00`)
}

function rangeDays(start: string, end: string) {
  return Math.round((Date.UTC(...ymd(end)) - Date.UTC(...ymd(start))) / 86400000) + 1
}

function ymd(iso: string): [number, number, number] {
  const [year, month, day] = iso.split('-').map(Number)
  return [year, (month || 1) - 1, day || 1]
}

const DAY_STYLE: Record<string, { label: string; className: string }> = {
  P: { label: 'Present', className: 'bg-emerald-500 text-white' },
  L: { label: 'Leave', className: 'bg-sky-400 text-white' },
  A: { label: 'Absent', className: 'bg-red-400 text-white' },
  O: { label: 'Weekly off', className: 'bg-slate-200 text-slate-500' },
  H: { label: 'Holiday', className: 'bg-violet-200 text-violet-700' },
  N: { label: 'Not joined yet', className: 'bg-slate-50 text-slate-300' },
  '-': { label: 'Upcoming', className: 'bg-white text-slate-300 ring-1 ring-inset ring-slate-200' },
}

function storedWeeklyOff(): number[] | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(WEEKLY_OFF_KEY) || 'null')
    if (Array.isArray(parsed)) return parsed.filter((day): day is number => Number.isInteger(day) && day >= 0 && day <= 6)
  } catch {
    // Ignore a corrupt saved value.
  }
  return null
}

function hoursLabel(minutes: number) {
  if (!minutes) return '0h'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

/** Days the worker was expected in: present on a working day, on leave, or absent. */
function expectedDays(row: AttendanceReportWorker) {
  return row.present_days - row.present_on_off_days + row.leave_days + row.absent_days
}

function attendanceRate(row: AttendanceReportWorker) {
  const expected = expectedDays(row)
  return expected ? Math.round(((row.present_days - row.present_on_off_days) / expected) * 100) : null
}

function workedInPeriod(row: AttendanceReportWorker) {
  return row.minutes > 0 || row.present_days > 0 || row.open_punches > 0
}

export default function AttendanceReportsPage() {
  const serviceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const today = todayIsoDate(useAttendanceOrgTimeZone())
  const [preset, setPreset] = useState<RangePreset>('today')
  const [pickedMonth, setMonth] = useState<string | null>(null)
  const [pickedStart, setStart] = useState<string | null>(null)
  const [pickedEnd, setEnd] = useState<string | null>(null)
  const month = pickedMonth ?? today.slice(0, 7)
  const range = preset === 'month'
    ? monthRange(month)
    : presetRange(preset, today) ?? { start: pickedStart ?? today, end: pickedEnd ?? today }
  const tooLong = rangeDays(range.start, range.end) > MAX_RANGE_DAYS
  const choosePreset = (next: RangePreset) => {
    if (next === 'custom') {
      setStart(range.start)
      setEnd(range.end)
    }
    if (next === 'month') setMonth(range.start.slice(0, 7))
    setPreset(next)
    setExpanded(null)
  }
  const [pickedWeeklyOff, setWeeklyOff] = useState<number[] | null>(storedWeeklyOff)
  const officeHoursQuery = useQuery({ queryKey: ['office-hours'], queryFn: officeHoursApi.get })
  const closedDays = (officeHoursQuery.data ?? DEFAULT_OFFICE_HOURS).days.filter((day) => !day.open).map((day) => day.day - 1)
  const weeklyOff = pickedWeeklyOff ?? closedDays
  const [search, setSearch] = useState('')
  const [workerScope, setWorkerScope] = useState<WorkerScope>('worked')
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showDemoLocations, setShowDemoLocations] = useState(false)

  const weeklyOffParam = [...weeklyOff].sort().join(',')
  const reportQuery = useQuery({
    queryKey: ['attendance-monthly-report', range.start, range.end, weeklyOffParam],
    queryFn: () => workersApi.getAttendanceMonthlyReport({ start_date: range.start, end_date: range.end, weekly_off: weeklyOffParam }),
    enabled: !tooLong && (pickedWeeklyOff != null || !officeHoursQuery.isLoading),
    placeholderData: (previous) => previous,
  })
  const report = reportQuery.data
  const locationsQuery = useQuery({
    queryKey: ['attendance-dashboard-locations', serviceInstanceId],
    queryFn: () => locationsApi.list({ service_instance_id: serviceInstanceId ?? undefined }),
  })
  const punchesQuery = useAttendancePunches(range.start, range.end)
  const locations = useMemo(
    () => (locationsQuery.data ?? []).filter((location) => !location.deleted && location.active !== false),
    [locationsQuery.data],
  )
  const rosterQueries = useQueries({
    queries: locations.map((location) => ({
      queryKey: ['attendance-dashboard-roster', serviceInstanceId, location.id, range.start, range.end],
      queryFn: () => attendanceBoardApi.listRoster(location.id, range.start, range.end),
      staleTime: 30_000,
    })),
  })

  const toggleDay = (day: number) => {
    const next = weeklyOff.includes(day) ? weeklyOff.filter((value) => value !== day) : [...weeklyOff, day]
    setWeeklyOff(next)
    localStorage.setItem(WEEKLY_OFF_KEY, JSON.stringify(next))
  }

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (report?.workers ?? []).filter((row) => {
      if (workerScope === 'worked' && !workedInPeriod(row)) return false
      return !term || `${row.name} ${row.worker_code || ''}`.toLowerCase().includes(term)
    })
  }, [report, search, workerScope])

  const totals = useMemo(() => rows.reduce(
    (sum, row) => ({
      present: sum.present + row.present_days,
      leave: sum.leave + row.leave_days,
      absent: sum.absent + row.absent_days,
      minutes: sum.minutes + row.minutes,
    }),
    { present: 0, leave: 0, absent: 0, minutes: 0 },
  ), [rows])

  const dayNumbers = useMemo(() => {
    if (!report) return []
    const [year, monthIndex, startDay] = ymd(report.start_date)
    return Array.from({ length: rangeDays(report.start_date, report.end_date) }, (_, index) => {
      const date = new Date(Date.UTC(year, monthIndex, startDay + index))
      return {
        iso: date.toISOString().slice(0, 10),
        day: date.getUTCDate(),
        month: date.getUTCMonth(),
        weekday: (date.getUTCDay() + 6) % 7,
      }
    })
  }, [report])
  const spansMonths = dayNumbers.length > 0 && dayNumbers[0].month !== dayNumbers[dayNumbers.length - 1].month
  const attendedByLocation = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const punch of punchesQuery.data?.results ?? []) {
      const locationId = punch.attendance_location_id
      const workerId = punchWorkerId(punch)
      if (!locationId || !workerId || !punch.check_in) continue
      const key = String(locationId)
      const bucket = map.get(key) ?? new Set<string>()
      bucket.add(workerId)
      map.set(key, bucket)
    }
    return map
  }, [punchesQuery.data?.results])
  const locationCards = useMemo(() => locations.map((location, index) => {
    const rosterQuery = rosterQueries[index]
    const attended = attendedByLocation.get(String(location.id))?.size ?? 0
    return {
      id: String(location.id),
      place: location.place || `Location ${location.id}`,
      ...countRosterWorkers(rosterQuery.data ?? []),
      attended,
      isLoading: rosterQuery.isLoading,
      isError: rosterQuery.isError,
    }
  }), [attendedByLocation, locations, rosterQueries])
  const locationLogsPath = (locationId: string) => {
    const params = new URLSearchParams({
      start_date: range.start,
      end_date: range.end,
      location_id: locationId,
      checked_in_only: 'true',
    })
    return `/job/attendance/logs?${params.toString()}`
  }
  const locationsLoading = locationsQuery.isLoading || punchesQuery.isLoading

  const exportCsv = () => {
    if (!report) return
    const header = ['Employee', 'Code', 'Expected days', 'Present', 'Present on off days', 'Leave', 'Absent', 'Hours', 'Attendance %', ...dayNumbers.map(({ iso }) => iso)]
    const body = rows.map((row) => [
      row.name,
      row.worker_code || '',
      expectedDays(row),
      row.present_days,
      row.present_on_off_days,
      row.leave_days,
      row.absent_days,
      (row.minutes / 60).toFixed(2),
      attendanceRate(row) ?? '',
      ...row.days.split(''),
    ])
    const csv = [header, ...body].map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `attendance-report-${report.start_date}_to_${report.end_date}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Attendance Dashboard</h1>
        </div>
        <Button type="button" variant="outline" onClick={exportCsv} disabled={!report || rows.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      <section className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          Period
          <Select value={preset} onValueChange={(value) => choosePreset(value as RangePreset)}>
            <SelectTrigger className="w-[180px]" aria-label="Report period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REPORT_PERIODS.map(({ group, options }) => (
                <SelectGroup key={group}>
                  <SelectLabel className="text-[11px] font-semibold uppercase tracking-wide">{group}</SelectLabel>
                  {options.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {preset === 'month' ? (
            <Input
              type="month"
              className="w-[170px]"
              aria-label="Month"
              value={month}
              max={today.slice(0, 7)}
              onChange={(event) => { setMonth(event.target.value || null); setExpanded(null) }}
            />
          ) : null}
          {preset === 'custom' ? (
            <>
              <Input
                type="date"
                className="w-[160px]"
                aria-label="From date"
                value={range.start}
                max={range.end}
                onChange={(event) => {
                  const next = event.target.value || today
                  setStart(next)
                  if (next > range.end) setEnd(next)
                  setExpanded(null)
                }}
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                className="w-[160px]"
                aria-label="To date"
                value={range.end}
                min={range.start}
                onChange={(event) => {
                  const next = event.target.value || today
                  setEnd(next)
                  if (next < range.start) setStart(next)
                  setExpanded(null)
                }}
              />
            </>
          ) : (
            <span className="text-xs text-slate-500">{shortDate(range.start)} – {shortDate(range.end)}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          Weekly off
          <div className="flex gap-1" role="group" aria-label="Weekly off days">
            {WEEKDAYS.map((label, day) => {
              const off = weeklyOff.includes(day)
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={off}
                  onClick={() => toggleDay(day)}
                  className={cn(
                    'h-8 w-11 rounded-md border text-xs font-medium transition-colors',
                    off ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          Workers
          <Select value={workerScope} onValueChange={(value) => { setWorkerScope(value as WorkerScope); setExpanded(null) }}>
            <SelectTrigger className="w-[190px]" aria-label="Worker scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="worked">Worked workers only</SelectItem>
              <SelectItem value="all">All workers</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input className="ml-auto max-w-xs" placeholder="Search employee or ID..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </section>

      {report ? (
        <div className="grid gap-3 md:grid-cols-4">
          <Stat label="Working days" value={String(report.working_days)} />
          <Stat label="Days present" value={String(totals.present)} />
          <Stat label="Leave / absent" value={`${totals.leave} / ${totals.absent}`} />
          <Stat label="Hours logged" value={hoursLabel(totals.minutes)} />
        </div>
      ) : null}

      <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Location cards</h2>
          </div>
          <div className="flex items-center gap-2">
            {import.meta.env.DEV ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-pressed={showDemoLocations}
                onClick={() => setShowDemoLocations((visible) => !visible)}
              >
                {showDemoLocations ? 'Hide demo data' : 'Show demo data'}
              </Button>
            ) : null}
            <Button asChild type="button" size="sm" variant="outline">
              <Link to="/job/locations">Add locations</Link>
            </Button>
            <Button asChild type="button" size="sm" variant="outline">
              <Link to="/job/workers">Add workers</Link>
            </Button>
          </div>
        </div>
        {locationsLoading ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-28 w-full" />)}
          </div>
        ) : locationsQuery.isError ? (
          <p className="text-sm text-destructive">{getErrorMessage(locationsQuery.error)}</p>
        ) : locationCards.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-600">No active locations.</p>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {locationCards.map((card) => (
                <Link
                  key={card.id}
                  to={locationLogsPath(card.id)}
                  aria-label={`${card.place}: ${card.isLoading ? 'loading allocation counts' : card.isError ? 'allocation counts unavailable' : `${card.allocated} allocated, ${card.own} own workers, ${card.contractor} contractor workers`}, ${card.attended} attended. View location attendance`}
                  className="block rounded-xl border border-slate-200 p-4 transition-colors hover:border-primary/50 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="text-sm font-semibold text-slate-900">{card.place}</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Allocated</div>
                      <div className="text-xl font-semibold tabular-nums text-slate-900">
                        {card.isLoading ? '…' : card.isError ? '—' : card.allocated}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Attended</div>
                      <div className="text-xl font-semibold tabular-nums text-emerald-700">{card.attended}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Own workers</div>
                      <div className="text-xl font-semibold tabular-nums text-slate-900">
                        {card.isLoading ? '…' : card.isError ? '—' : card.own}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Contractor workers</div>
                      <div className="text-xl font-semibold tabular-nums text-slate-900">
                        {card.isLoading ? '…' : card.isError ? '—' : card.contractor}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>
      {import.meta.env.DEV && showDemoLocations ? (
        <section className="space-y-3 rounded-2xl border border-dashed border-amber-300 bg-amber-50/60 p-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Local demo preview</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {DEMO_LOCATIONS.map((location) => {
              return (
                <div key={location.id} className="rounded-xl border border-amber-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">{location.place}</h3>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Allocated</div>
                      <div className="text-xl font-semibold tabular-nums text-slate-900">{location.allocated}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Attended</div>
                      <div className="text-xl font-semibold tabular-nums text-emerald-700">{location.attended}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Own workers</div>
                      <div className="text-xl font-semibold tabular-nums text-slate-900">{location.own}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Contractor workers</div>
                      <div className="text-xl font-semibold tabular-nums text-slate-900">{location.contractor}</div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      ) : null}

      {report && report.holidays.length > 0 ? (
        <p className="text-xs text-slate-500">
          Holidays: {report.holidays.map((holiday) => `${shortDate(holiday.date)} – ${holiday.title}`).join(', ')}
        </p>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {tooLong ? (
          <p className="p-6 text-sm text-amber-700">Pick a period of at most {MAX_RANGE_DAYS} days.</p>
        ) : reportQuery.isPending ? (
          <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}</div>
        ) : reportQuery.error ? (
          <p className="p-6 text-sm text-destructive">{getErrorMessage(reportQuery.error)}</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">
            {workerScope === 'worked'
              ? 'No workers worked during this period.'
              : 'No employees to report for this period.'}
          </p>
        ) : (
          <div className={cn('overflow-x-auto', reportQuery.isFetching && 'opacity-60')}>
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {['Employee', 'Expected', 'Present', 'Leave', 'Absent', 'Hours logged', 'Avg / present day', 'Attendance'].map((heading) => (
                    <th key={heading} className="px-4 py-3 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rate = attendanceRate(row)
                  const open = expanded === row.worker_id
                  return (
                    <Fragment key={row.worker_id}>
                      <tr
                        className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                        onClick={() => setExpanded(open ? null : row.worker_id)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            {open ? <ChevronDown className="mt-0.5 h-4 w-4 text-slate-400" /> : <ChevronRight className="mt-0.5 h-4 w-4 text-slate-400" />}
                            <div>
                              <WorkerProfileLink workerId={row.worker_id} name={row.name} className="font-medium text-slate-900" />
                              <div className="text-xs text-slate-500">{row.worker_code || row.worker_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 tabular-nums">{expectedDays(row)}</td>
                        <td className="px-4 py-3 tabular-nums">
                          <span className="font-medium text-emerald-700">{row.present_days}</span>
                          {row.present_on_off_days ? (
                            <div className="text-[11px] text-slate-500">incl. {row.present_on_off_days} on off days</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-sky-700">{row.leave_days}</td>
                        <td className={cn('px-4 py-3 tabular-nums', row.absent_days ? 'font-medium text-red-600' : 'text-slate-500')}>{row.absent_days}</td>
                        <td className="px-4 py-3 tabular-nums font-medium">
                          {hoursLabel(row.minutes)}
                          {row.open_punches ? <div className="text-[11px] font-normal text-amber-600">{row.open_punches} open punch{row.open_punches === 1 ? '' : 'es'} not counted</div> : null}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-slate-600">{row.present_days ? hoursLabel(Math.round(row.minutes / row.present_days)) : '—'}</td>
                        <td className="px-4 py-3">
                          {rate == null ? '—' : (
                            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', rate >= 90 ? 'bg-emerald-50 text-emerald-700' : rate >= 75 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700')}>
                              {rate}%
                            </span>
                          )}
                        </td>
                      </tr>
                      {open ? (
                        <tr className="bg-slate-50/60">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {row.days.split('').map((mark, index) => {
                                const style = DAY_STYLE[mark] ?? DAY_STYLE['-']
                                const info = dayNumbers[index]
                                return (
                                  <div
                                    key={index}
                                    title={`${WEEKDAYS[info?.weekday ?? 0]} ${info ? shortDate(info.iso) : ''}: ${style.label}`}
                                    className={cn('flex h-10 min-w-8 flex-col items-center justify-center rounded px-0.5 text-[10px] leading-tight', style.className)}
                                  >
                                    <span className="font-semibold">
                                      {info?.day}
                                      {spansMonths && info && (index === 0 || info.day === 1) ? ` ${MONTHS[info.month]}` : ''}
                                    </span>
                                    <span>{WEEKDAYS[info?.weekday ?? 0].slice(0, 2)}</span>
                                  </div>
                                )
                              })}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
                              {Object.entries(DAY_STYLE).map(([mark, style]) => (
                                <span key={mark} className="flex items-center gap-1">
                                  <span className={cn('inline-block h-3 w-3 rounded-sm', style.className)} />
                                  {style.label}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  )
}
